import { type ResultsView } from '@kijiji/generated/graphql-types'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { type PropsWithChildren, type ReactNode } from 'react'

import { AppDownloadBanner } from '@/components/shared/app-download-banner/AppDownloadBanner'
import { GlobalHeader } from '@/components/shared/global-header'
import { BaseLayoutBody, BaseLayoutWrapper } from '@/components/shared/layouts/styled'
import { PSABanner } from '@/components/shared/psa-banner/PSABanner'
import { SearchLocationModal } from '@/components/shared/search-location-modal'
import { PSA_BANNER_KEYS } from '@/constants/psaBanner'
import { AuthModal } from '@/features/auth/components/auth-modal/AuthModal'
import { MegaMenu } from '@/features/navigation/components/mega-menu/desktop/MegaMenu'
import { SpeedCurveScripts } from '@/features/speedcurve/scripts/SpeedCurveScripts'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { RemoteParamKeys } from '@/lib/firebase/config'
import { useToggle } from '@/lib/firebase/hooks/useToggle'
import { Flex } from '@/ui/atoms/flex'
import { SkipNavContent, SkipNavLink } from '@/ui/atoms/skip-nav'

const GlobalFooter = dynamic(
  () => import('@/components/shared/global-footer').then((mod) => mod.GlobalFooter),
  { ssr: false }
)

const CURRENT_PSA_CAMPAIGN = PSA_BANNER_KEYS.SITE_MAINTENANCE

export type BaseLayoutProps = PropsWithChildren<{
  /**
   * Defines if the mega menu should be rendered on the search bar
   */
  searchBarChildren?: ReactNode
  /**
   * Will show the search bar in the header. Defaults to false.
   */
  showSearchBar?: boolean
  /**
   * Render an Auth Modal on the page that can be triggered on
   */
  withAuthModal?: boolean
  /**
   *  If true, the main content will fill the full container.
   */
  isMainFullHeight?: boolean
  /**
   * Include the RUM data tracking scripts to speedcurve and corewebvitals.io
   */
  includeRUMScripts?: boolean

  /**
   * Page name used to identify the page (currently used for the header)
   */
  pageName?: string

  /**
   * Defines custom body color for different pages
   */
  bodyColor?: string

  /**
   * Category ID for the page
   */
  categoryId?: number

  /**
   * The view type of the page
   */
  viewType?: ResultsView
}>

/**
 * Layout defined for general pages with header + content + footer. Must be used on all root pages.
 */

export const BaseLayout = ({
  children,
  isMainFullHeight = false,
  showSearchBar = false,
  withAuthModal = false,
  includeRUMScripts = false,
  bodyColor,
  categoryId,
  viewType,
}: BaseLayoutProps) => {
  const { t } = useTranslation('common')
  const { location } = useGetLocation()
  const router = useRouter()

  const navigationDecision = useToggle(RemoteParamKeys.NAVIGATION_SRP)
  const isNavigationSrpEnabled = navigationDecision?.enabled === true

  // Check if we're on the SRP or Homepage
  const isSRPPage = router.pathname.startsWith('/srp/')
  const isHomePage = router.pathname === '/' || router.pathname === '/home/[[...params]]'

  // Only show mega menu on homepage OR on SRP when the feature flag is enabled
  const shouldShowMegaMenu = isHomePage || (isSRPPage && isNavigationSrpEnabled)

  return (
    <>
      <PSABanner psaKey={CURRENT_PSA_CAMPAIGN} />
      <BaseLayoutWrapper bodyColor={bodyColor}>
        <SkipNavLink contentId="skip-to-main">{t('skip_nav')}</SkipNavLink>

        <BaseLayoutBody>
          <AppDownloadBanner />

          <GlobalHeader
            psaCampaign={CURRENT_PSA_CAMPAIGN}
            showSearchBar={showSearchBar}
            hasNavigation={shouldShowMegaMenu}
          >
            {shouldShowMegaMenu && (
              <div data-testid="mega-menu-container">
                <MegaMenu locationId={location.id} icons={!isNavigationSrpEnabled} />
              </div>
            )}
          </GlobalHeader>

          <SkipNavContent id="skip-to-main" />

          {isMainFullHeight ? (
            <Flex
              flexGrow={1}
              flexBasis="0%"
              flexShrink={1}
              id="base-layout-main-wrapper"
              as="main"
            >
              {children}
            </Flex>
          ) : (
            <main id="base-layout-main-wrapper">{children}</main>
          )}
          {withAuthModal && <AuthModal />}
          <GlobalFooter />
        </BaseLayoutBody>
      </BaseLayoutWrapper>

      {includeRUMScripts && process.env.NODE_ENV === 'production' && (
        <SpeedCurveScripts categoryId={categoryId} viewType={viewType} />
      )}

      {/* It should only be imported once per page, or else multiple instances of the modal will open */}
      <SearchLocationModal />
    </>
  )
}
